// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — AYNI's NOAA library as datasets this app can load: `LIB_<id>`
 * rows built from `src/data/noaa-library.json` (written by
 * `scripts/build-noaa-library.ts` from the signed library release at
 * `catalog.pachamama-studios.stream`), each playing one picture or one
 * MP4 straight from that bucket. Browse lists the NOAA collection
 * (`noaaCatalogDatasets`); the lessons name datasets by `sos:<id>`
 * (`ensureLibraryDataset`). The index is loaded on first use, in its own
 * chunk. Owner rule (7 October 2026): AYNI reads everything from its own
 * buckets; NOAA is credited as the source.
 *
 * A lesson registers its rows before loading one (`ensureLibraryDataset`),
 * and so does a class following its teacher: a student's browser never
 * fetched the lesson, but the room names the row's id, and the library is
 * public (the same files the R2 bucket serves to anyone).
 */
import type { Dataset, DatasetFormat, EnrichedMetadata } from '../types'
import { dataService } from './dataService'
import { sosBounds } from './datasetCoverage'
import { localized } from './khipu'

export const LIBRARY_PREFIX = 'LIB_'

/** The library host every row's files come from. */
export const LIBRARY_HOST = 'catalog.pachamama-studios.stream'

/** Who the NOAA datasets come from, when a descriptor names no creator. */
export const NOAA_SOURCE = 'NOAA Science On a Sphere'

interface Credit {
  name: string
  url?: string
}

/** One dataset of the index, as `scripts/lib/noaaLibrary.ts` writes it. */
export interface LibraryEntry {
  title: Record<string, string>
  kind: 'image' | 'video'
  media: string
  mediaType: string
  bytes?: number
  legend?: string
  thumbnail?: string
  description?: Record<string, string>
  creator?: string
  page?: string
  categories?: string[]
  lessonOnly?: true
  keywords?: string[]
  tags?: string[]
  captions?: string
  startTime?: string
  endTime?: string
  period?: string
  legacyIds?: string[]
  weight?: number
  celestialBody?: string
  /** AYNI (2026-10-24): a picture AYNI made from another source, not a NOAA SOS dataset (Uranus, Neptune from NASA). */
  origin?: 'ayni'
  radiusMi?: number
  lonOrigin?: number
  isFlippedInY?: boolean
  sosCategories?: Record<string, string[]>
  developer?: Credit
  visualization?: Credit
  dateAdded?: string
  related?: Array<{ title: string; url: string }>
  /** AYNI (2026-10-30): the stream that replaces this row for a plan that plays it (`libraryTwins.ts`). */
  twin?: string
}

export interface LessonLibraryIndex {
  release: string
  base: string
  datasets: Record<string, LibraryEntry>
  unavailable: Record<string, { title?: Record<string, string>; reason: string; note?: string }>
}

let loading: Promise<LessonLibraryIndex> | null = null

/** The library index, loaded once. */
export function loadLessonLibrary(): Promise<LessonLibraryIndex> {
  loading ??= import('../data/noaa-library.json')
    .then((m) => (m.default ?? m) as unknown as LessonLibraryIndex)
    .catch((error: unknown) => {
      // A failed chunk (offline, a new deploy) is tried again next time.
      loading = null
      throw error
    })
  return loading
}

/**
 * AYNI (2026-10-24): is a dataset's page on NOAA's Science On a Sphere
 * catalogue? A library picture AYNI made from another source (the NASA
 * Voyager 2 maps of Uranus and Neptune, `origin: 'ayni'` in the index)
 * links to that source instead, and Browse and the download note must not
 * call it NOAA's.
 */
export function isSosCatalogPage(url: string | undefined): boolean {
  if (!url) return false
  try {
    return /(^|\.)sos\.noaa\.gov$/.test(new URL(url).host)
  } catch {
    return false
  }
}

/** The app's id for a library dataset. */
export function libraryDatasetId(sosId: string): string {
  return `${LIBRARY_PREFIX}${sosId}`
}

/** Whether a URL is a file in the library: one stored object, no resolution ladder, no streaming manifest. */
export function isLibraryMediaUrl(url: string): boolean {
  try {
    return new URL(url).host === LIBRARY_HOST
  } catch {
    return false
  }
}

const IMAGE_FORMATS: ReadonlySet<DatasetFormat> = new Set(['image/png', 'image/jpeg', 'image/webp'])

const creditOf = (c: Credit | undefined) => (c ? { name: c.name, ...(c.url ? { affiliationUrl: c.url } : {}) } : undefined)

/** A library entry as a dataset row. */
export function libraryDataset(sosId: string, entry: LibraryEntry, base: string, lang = 'en'): Dataset {
  const format: DatasetFormat = entry.kind === 'video'
    ? 'video/mp4'
    : IMAGE_FORMATS.has(entry.mediaType as DatasetFormat) ? (entry.mediaType as DatasetFormat) : 'image/png'
  const description = entry.description ? localized(entry.description, lang) : undefined
  const enriched: EnrichedMetadata = {
    ...(description ? { description } : {}),
    ...(entry.sosCategories && Object.keys(entry.sosCategories).length ? { categories: entry.sosCategories } : {}),
    ...(entry.keywords?.length ? { keywords: entry.keywords } : {}),
    ...(entry.related?.length ? { relatedDatasets: entry.related } : {}),
    ...(entry.developer ? { datasetDeveloper: creditOf(entry.developer) } : {}),
    ...(entry.visualization ? { visDeveloper: creditOf(entry.visualization) } : {}),
    ...(entry.dateAdded ? { dateAdded: entry.dateAdded } : {}),
    ...(entry.page ? { catalogUrl: entry.page } : {}),
  }
  const bounds = sosBounds({ format, celestialBody: entry.celestialBody })
  return {
    id: libraryDatasetId(sosId),
    ...(entry.legacyIds?.length ? { legacyId: entry.legacyIds[0], legacyIds: entry.legacyIds } : {}),
    title: localized(entry.title, lang),
    format,
    dataLink: base + entry.media,
    // AYNI: NOAA stays credited as the source of every library dataset.
    organization: entry.creator || NOAA_SOURCE,
    ...(entry.thumbnail ? { thumbnailLink: base + entry.thumbnail } : {}),
    ...(entry.legend ? { legendLink: base + entry.legend } : {}),
    ...(entry.captions ? { closedCaptionLink: base + entry.captions } : {}),
    ...(description ? { abstractTxt: description } : {}),
    ...(entry.page ? { websiteLink: entry.page } : {}),
    tags: entry.tags?.length ? entry.tags : entry.categories ?? [],
    ...(entry.startTime ? { startTime: entry.startTime } : {}),
    ...(entry.endTime ? { endTime: entry.endTime } : {}),
    ...(entry.period ? { period: entry.period } : {}),
    weight: entry.weight ?? 0,
    ...(entry.celestialBody ? { celestialBody: entry.celestialBody } : {}),
    ...(typeof entry.radiusMi === 'number' ? { radiusMi: entry.radiusMi } : {}),
    ...(typeof entry.lonOrigin === 'number' ? { lonOrigin: entry.lonOrigin } : {}),
    ...(entry.isFlippedInY ? { isFlippedInY: true } : {}),
    ...(bounds ? { boundingBox: bounds } : {}),
    ...(entry.twin ? { libraryTwin: entry.twin } : {}),
    ...(Object.keys(enriched).length ? { enriched } : {}),
  }
}

/**
 * The NOAA library's datasets for Browse: every dataset of the release's
 * NOAA collection the index lists (the rights exclusions are already
 * out), not the ones only a lesson names.
 */
export async function noaaCatalogDatasets(lang = 'en'): Promise<Dataset[]> {
  const library = await loadLessonLibrary()
  return Object.entries(library.datasets)
    .filter(([, entry]) => !entry.lessonOnly)
    .map(([id, entry]) => libraryDataset(id, entry, library.base, lang))
}

/**
 * The library row for one of NOAA's old catalogue ids (`INTERNAL_SOS_*`,
 * `SOS_ONLY_*`), or null when the library has no such dataset (dropped
 * with the old list, or held out by the rights screen). Story Khipus and old
 * session records name their datasets this way.
 */
export async function libraryIdForLegacy(oldId: string): Promise<string | null> {
  const library = await loadLessonLibrary()
  for (const [id, entry] of Object.entries(library.datasets)) {
    if (entry.legacyIds?.includes(oldId)) return libraryDatasetId(id)
  }
  return null
}

/**
 * Make a library row loadable: true once `dataService` knows it, false
 * for an id that is not a library row or that the library cannot play.
 */
export async function ensureLibraryDataset(datasetId: string): Promise<boolean> {
  if (!datasetId.startsWith(LIBRARY_PREFIX)) return false
  if (dataService.getDatasetById(datasetId)) return true
  const sosId = datasetId.slice(LIBRARY_PREFIX.length)
  const library = await loadLessonLibrary()
  const entry = Object.prototype.hasOwnProperty.call(library.datasets, sosId) ? library.datasets[sosId] : undefined
  if (!entry) return false
  dataService.injectDataset(libraryDataset(sosId, entry, library.base))
  return true
}
